import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUSPICIOUS_LABELS,
  type CashierControlDTO,
  type PosSettings,
  type ProductDTO,
  type ShortageRowDTO,
  type StockCountDTO,
  type SuspiciousEventDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '@bifeai/ui';
import { ClipboardCheck, ListChecks, Search, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useCategories, useProducts } from '../../lib/catalog';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { formatQty, todayIso, toInternalQty } from '../../lib/units';

type Tab = 'count' | 'shortages' | 'cashiers';
const firstOfMonth = () => todayIso().slice(0, 8) + '01';
const pct = (r: number) => `${(r * 100).toLocaleString('es-AR', { maximumFractionDigits: 1 })} %`;

/**
 * Control de faltantes.
 * - Recuento: se cuenta a ciegas lo que hay; la diferencia con el sistema es el faltante (merma, va al tacho).
 * - Faltantes por producto: lo que entró, lo que se vendió y lo que faltó al contar.
 * - Cajeros (solo el dueño): anulaciones, descuentos, pesos a mano, etiquetas repetidas, retiros y faltantes de caja.
 */
export function ControlPage() {
  const { can } = useSession();
  const canCount = can('purchases.manage');
  const canCashiers = can('audit.view');
  const [tab, setTab] = useState<Tab>(canCount ? 'count' : 'cashiers');
  const tabs = [
    ...(canCount
      ? ([
          ['count', 'Recuento'],
          ['shortages', 'Faltantes por producto'],
        ] as const)
      : []),
    ...(canCashiers ? ([['cashiers', 'Movimientos por cajero']] as const) : []),
  ];
  return (
    <>
      <PageHeader
        title="Control de faltantes"
        subtitle="Contá lo que hay y el sistema te dice qué falta, de qué producto y cuánta plata es. Y mirá qué movimientos raros hubo en cada caja."
      />
      <div className="bf-tabs" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'count' ? <CountTab /> : tab === 'shortages' ? <ShortagesTab /> : <CashiersTab />}
    </>
  );
}

function Period({ from, to, setFrom, setTo }: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  return (
    <div className="bf-period">
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Desde <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
      </label>
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Hasta <Input type="date" value={to} min={from} max={todayIso()} onChange={(e) => e.target.value && setTo(e.target.value)} />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------- recuento
interface DraftLine {
  productId: string;
  value: string;
}
const DRAFT_KEY = 'bifeai.recuento.borrador';
function loadDraft(): DraftLine[] {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as DraftLine[]) : [];
  } catch {
    return [];
  }
}
function saveDraft(lines: DraftLine[]) {
  try {
    if (lines.length) localStorage.setItem(DRAFT_KEY, JSON.stringify(lines));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* sin almacenamiento: el borrador vive solo en pantalla */
  }
}

function CountTab() {
  const qc = useQueryClient();
  const { me, multiBranch, selectedBranchId, isHeadquartersUser } = useSession();
  const products = useProducts();
  const categories = useCategories();
  const countable = useMemo(() => (products.data ?? []).filter((p) => p.tracksStock && p.isActive), [products.data]);
  const hq = me.branches.find((b) => b.isHeadquarters) ?? me.branches[0];
  const [branchId, setBranchId] = useState(me.membership.branchId ?? selectedBranchId ?? hq?.id ?? '');
  const [lines, setLines] = useState<DraftLine[]>(loadDraft);
  const [search, setSearch] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<StockCountDTO | null>(null);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [openId, setOpenId] = useState<string | null>(null);
  const history = useQuery({
    queryKey: ['control', 'counts', from, to],
    queryFn: () => api.get<StockCountDTO[]>(`/control/counts?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });

  useEffect(() => saveDraft(lines), [lines]);
  const byId = (id: string) => countable.find((p) => p.id === id);
  const inList = new Set(lines.map((l) => l.productId));
  const matches = search.trim()
    ? countable.filter((p) => !inList.has(p.id) && p.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 8)
    : [];
  const add = (ps: ProductDTO[]) =>
    setLines((ls) => [...ls, ...ps.filter((p) => !ls.some((l) => l.productId === p.id)).map((p) => ({ productId: p.id, value: '' }))]);
  const usedCategories = (categories.data ?? []).filter((c) => countable.some((p) => p.categoryId === c.id));
  const missing = lines.filter((l) => !l.value.trim()).length;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!lines.length) return setError('Agregá los productos que contaste.');
    if (missing) return setError(`Falta poner cuánto hay de ${missing} ${missing === 1 ? 'producto' : 'productos'} (si no hay nada, poné 0).`);
    const payload = [];
    for (const l of lines) {
      const p = byId(l.productId);
      if (!p) continue;
      const q = toInternalQty(p.unitType, l.value);
      if (q === null || q < 0) return setError(`Revisá la cantidad de ${p.name}.`);
      payload.push({ productId: p.id, countedQty: q });
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<StockCountDTO>('/control/counts', { branchId, notes: notes.trim() || null, lines: payload });
      setLines([]);
      setNotes('');
      setResult(res);
      await Promise.all(['control', 'stock', 'waste', 'notifications', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function nextOnEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const inputs = Array.from(document.querySelectorAll<HTMLInputElement>('[data-count-qty]'));
    inputs[inputs.indexOf(e.currentTarget) + 1]?.focus();
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Nuevo recuento"
          subtitle="Contá lo que hay en la cámara y en la heladera. Podés contar uno, varios o todos los productos, cuando quieras. No se muestra lo que dice el sistema: así el recuento es honesto. Al confirmar, te dice qué falta."
        />
        <CardBody>
          <form className="bf-form" onSubmit={submit} noValidate>
            <div className="bf-form-row">
              <Field label="Buscar producto">
                <div style={{ position: 'relative' }}>
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ej.: vacío" aria-label="Buscar producto" />
                  {matches.length > 0 && (
                    <div className="bf-suggest">
                      {matches.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            add([p]);
                            setSearch('');
                          }}
                        >
                          <Search aria-hidden /> {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
              {multiBranch && isHeadquartersUser && (
                <Field label="Sucursal">
                  <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                    {me.branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
            {usedCategories.length > 0 && (
              <div className="bf-chips">
                <span className="bf-cell-sub">Agregar todo:</span>
                {usedCategories.map((c) => (
                  <Button key={c.id} size="sm" icon={<ListChecks />} onClick={() => add(countable.filter((p) => p.categoryId === c.id))}>
                    {c.name}
                  </Button>
                ))}
              </div>
            )}
            {lines.length === 0 ? (
              <EmptyState icon={<ClipboardCheck />} title="Agregá lo que vas a contar" text="Buscalo por nombre o agregá una categoría entera." />
            ) : (
              <div className="bf-scroll-box bf-cut-table">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ textAlign: 'right' }}>Hay (contado)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => {
                      const p = byId(l.productId);
                      if (!p) return null;
                      return (
                        <tr key={l.productId}>
                          <td>
                            <span className="bf-cell-main">{p.name}</span>
                            <div className="bf-cell-sub">{p.unitType === 'WEIGHT' ? 'en kilos' : 'en unidades'}</div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <Input
                              data-count-qty
                              inputMode="decimal"
                              value={l.value}
                              onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                              onKeyDown={nextOnEnter}
                              placeholder={p.unitType === 'WEIGHT' ? 'kg' : 'u.'}
                              aria-label={`Cuánto hay de ${p.name}`}
                              style={{ maxWidth: 120, marginLeft: 'auto' }}
                            />
                          </td>
                          <td style={{ textAlign: 'right', width: 48 }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Trash2 />}
                              aria-label={`Sacar ${p.name}`}
                              onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Field label="Notas (opcional)">
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej.: recuento del sábado al cierre" />
            </Field>
            <Callout tone="info">
              Contá con el mostrador quieto (antes de abrir o al cerrar): el sistema compara con lo que había en el momento de confirmar.
            </Callout>
            {error && <Callout tone="danger">{error}</Callout>}
            <div className="bf-form-actions">
              <span className={missing ? 'bf-text-warning' : 'bf-cell-sub'} style={{ marginRight: 'auto' }}>
                {lines.length ? (missing ? `Faltan ${missing} por contar` : `${lines.length} productos contados`) : ''}
              </span>
              {lines.length > 0 && (
                <Button onClick={() => setLines([])} disabled={busy}>
                  Vaciar
                </Button>
              )}
              <Button type="submit" variant="primary" icon={<ClipboardCheck />} loading={busy} disabled={!lines.length}>
                Confirmar recuento
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Recuentos hechos" />
        <CardBody>
          <Period from={from} to={to} setFrom={setFrom} setTo={setTo} />
        </CardBody>
        <CardBody flush>
          {!history.data ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : history.data.length === 0 ? (
            <EmptyState icon={<ClipboardCheck />} title="No hay recuentos en estas fechas" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Contó</th>
                    <th style={{ textAlign: 'right' }}>Productos</th>
                    <th style={{ textAlign: 'right' }}>Faltó</th>
                    <th style={{ textAlign: 'right' }}>Sobró</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((c) => (
                    <tr key={c.id} data-clickable="true" onClick={() => setOpenId(c.id)}>
                      <td className="bf-cell-sub">
                        {formatDateTime(c.createdAt)}
                        {multiBranch && <div>{c.branchName}</div>}
                      </td>
                      <td>
                        {c.createdByName ?? '—'} {c.alerts > 0 && <Badge tone="danger">{c.alerts} con faltante</Badge>}
                        {c.notes && <div className="bf-cell-sub">{c.notes}</div>}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {c.lines}
                      </td>
                      <td className="bf-num bf-text-danger" style={{ textAlign: 'right' }}>
                        {c.shortageCostCents !== null ? formatMoney(c.shortageCostCents) : ''}
                      </td>
                      <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                        {c.surplusCostCents !== null ? formatMoney(c.surplusCostCents) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {result && <CountResultDialog count={result} onClose={() => setResult(null)} justDone />}
      {openId && <CountLoader id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function CountLoader({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['control', 'count', id], queryFn: () => api.get<StockCountDTO>(`/control/counts/${id}`) });
  if (!q.data) return null;
  return <CountResultDialog count={q.data} onClose={onClose} />;
}

function CountResultDialog({ count, onClose, justDone }: { count: StockCountDTO; onClose: () => void; justDone?: boolean }) {
  const lines = count.detail ?? [];
  const showMoney = count.shortageCostCents !== null;
  const short = lines.filter((l) => l.diffQty < 0).length;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={
        justDone
          ? short
            ? short === 1
              ? 'Falta 1 producto'
              : `Faltan ${short} productos`
            : 'Todo en orden'
          : `Recuento del ${formatDateTime(count.createdAt)}`
      }
      description={[count.branchName, count.createdByName && `contó ${count.createdByName}`].filter(Boolean).join(' · ')}
      wide
    >
      <div className="bf-form">
        {showMoney && (
          <div className="bf-close-summary">
            <div>
              <span>Faltó</span>
              <strong className="bf-num bf-text-danger">{formatMoney(count.shortageCostCents ?? 0)}</strong>
            </div>
            <div>
              <span>Sobró</span>
              <strong className="bf-num">{formatMoney(count.surplusCostCents ?? 0)}</strong>
            </div>
            <div>
              <span>Para revisar</span>
              <strong className="bf-num">{count.alerts}</strong>
            </div>
          </div>
        )}
        <div className="bf-scroll-box bf-cut-table">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ textAlign: 'right' }}>Sistema</th>
                <th style={{ textAlign: 'right' }}>Contado</th>
                <th style={{ textAlign: 'right' }}>Diferencia</th>
                <th style={{ textAlign: 'right' }}>De lo vendido</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.productId}>
                  <td>
                    <span className="bf-cell-main">{l.productName}</span> {l.alert && <Badge tone="danger">revisar</Badge>}
                    <div className="bf-cell-sub">
                      Vendido {formatQty(l.unitType, l.soldQty)}{' '}
                      {l.sinceAt ? `desde el recuento del ${formatDate(l.sinceAt)}` : 'en los últimos 30 días'}
                    </div>
                  </td>
                  <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                    {formatQty(l.unitType, l.systemQty)}
                  </td>
                  <td className="bf-num" style={{ textAlign: 'right' }}>
                    {formatQty(l.unitType, l.countedQty)}
                  </td>
                  <td className={`bf-num ${l.diffQty < 0 ? 'bf-text-danger' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                    {l.diffQty === 0 ? 'Justo' : `${l.diffQty > 0 ? '+' : '−'}${formatQty(l.unitType, Math.abs(l.diffQty))}`}
                    {l.diffCostCents !== null && l.diffQty !== 0 && <div className="bf-cell-sub">{formatMoney(Math.abs(l.diffCostCents))}</div>}
                  </td>
                  <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                    {l.shortageRatio !== null ? pct(l.shortageRatio) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="bf-cell-sub" style={{ margin: 0 }}>
          Lo que faltó se descontó del stock y cuenta como merma (Mermas y tacho). Lo que sobró se sumó. Los marcados “revisar” superan el % de aviso
          de Configuración y te llegó una alerta.
        </p>
        <div className="bf-form-actions">
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------- faltantes por producto
function ShortagesTab() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [onlyShort, setOnlyShort] = useState(true);
  const settings = useQuery({ queryKey: ['pos', 'settings'], queryFn: () => api.get<PosSettings>('/pos/settings') });
  const threshold = (settings.data?.stock.shortageAlertPercent ?? 3) / 100;
  const q = useQuery({
    queryKey: ['control', 'shortages', from, to],
    queryFn: () => api.get<ShortageRowDTO[]>(`/control/shortages?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });
  const rows = (q.data ?? []).filter((r) => !onlyShort || r.shortageQty > 0);
  const showMoney = (q.data ?? []).some((r) => r.shortageCostCents !== null);
  const total = (q.data ?? []).reduce((a, r) => a + (r.shortageCostCents ?? 0), 0);
  const neverCounted = (q.data ?? []).filter((r) => !r.lastCountAt && r.soldQty > 0).length;

  return (
    <>
      <Period from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <Callout tone="info">
        El sistema sabe lo que <strong>tendría</strong> que haber. Lo que falta de verdad solo aparece cuando contás: por eso conviene contar seguido,
        aunque sea de a pocos productos.
        {neverCounted === 1 && ' Hay 1 producto con ventas que nunca contaste.'}
        {neverCounted > 1 && ` Hay ${neverCounted} productos con ventas que nunca contaste.`}
      </Callout>
      <Card>
        <CardHeader
          title={showMoney ? `Faltante del período: ${formatMoney(total)}` : 'Faltante del período'}
          subtitle="Por producto: lo que entró, lo que salió y lo que faltó al contar."
          actions={
            <label className="bf-check">
              <input type="checkbox" checked={onlyShort} onChange={(e) => setOnlyShort(e.target.checked)} /> Solo con faltante
            </label>
          }
        />
        <CardBody flush>
          {!q.data ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck />}
              title={onlyShort ? 'No faltó nada en los recuentos de estas fechas' : 'Sin movimientos en estas fechas'}
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th style={{ textAlign: 'right' }}>Entró</th>
                    <th style={{ textAlign: 'right' }}>Vendido</th>
                    <th style={{ textAlign: 'right' }}>Bajas</th>
                    <th style={{ textAlign: 'right' }}>Usado</th>
                    <th style={{ textAlign: 'right' }}>Faltó</th>
                    <th style={{ textAlign: 'right' }}>De lo vendido</th>
                    <th style={{ textAlign: 'right' }}>Hay</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const high = r.shortageRatio !== null && r.shortageRatio >= threshold;
                    const f = (n: number) => (n ? formatQty(r.unitType, n) : '—');
                    return (
                      <tr key={r.productId}>
                        <td>
                          <span className="bf-cell-main">{r.productName}</span>
                          <div className="bf-cell-sub">
                            {r.lastCountAt ? `Último recuento: ${formatDate(r.lastCountAt)}` : <Badge tone="warning">nunca contado</Badge>}
                          </div>
                        </td>
                        <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                          {f(r.inQty)}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {f(r.soldQty)}
                        </td>
                        <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                          {f(r.wasteQty)}
                        </td>
                        <td className="bf-num bf-cell-sub" style={{ textAlign: 'right' }}>
                          {f(r.usedQty)}
                        </td>
                        <td className={`bf-num ${r.shortageQty ? 'bf-text-danger' : ''}`} style={{ textAlign: 'right', fontWeight: 600 }}>
                          {f(r.shortageQty)}
                          {r.shortageCostCents ? <div className="bf-cell-sub">{formatMoney(r.shortageCostCents)}</div> : null}
                          {r.surplusQty > 0 && <div className="bf-cell-sub">sobró {formatQty(r.unitType, r.surplusQty)}</div>}
                        </td>
                        <td
                          className={`bf-num ${high ? 'bf-text-danger' : 'bf-cell-sub'}`}
                          style={{ textAlign: 'right', fontWeight: high ? 600 : undefined }}
                        >
                          {r.shortageRatio !== null ? pct(r.shortageRatio) : ''}
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {formatQty(r.unitType, r.onHandQty)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- cajeros
type Metric = 'voids' | 'discounts' | 'manual';
const rate = (c: CashierControlDTO, m: Metric) =>
  m === 'voids'
    ? c.voids / Math.max(1, c.sales + c.voids)
    : m === 'discounts'
      ? c.discounts / Math.max(1, c.sales)
      : c.manualWeights / Math.max(1, c.weightLines);

function CashiersTab() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [open, setOpen] = useState<CashierControlDTO | null>(null);
  const q = useQuery({
    queryKey: ['control', 'cashiers', from, to],
    queryFn: () => api.get<CashierControlDTO[]>(`/control/cashiers?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
  });
  const rows = q.data ?? [];
  // Se marca a quien está muy por encima del resto (el doble del promedio del equipo).
  const avg = (m: Metric) => {
    const withData = rows.filter((r) => (m === 'manual' ? r.weightLines : r.sales) > 0);
    return withData.length ? withData.reduce((a, r) => a + rate(r, m), 0) / withData.length : 0;
  };
  const avgs = { voids: avg('voids'), discounts: avg('discounts'), manual: avg('manual') };
  const high = (c: CashierControlDTO, m: Metric) => rows.length > 1 && rate(c, m) > 0 && rate(c, m) >= avgs[m] * 2;

  return (
    <>
      <Period from={from} to={to} setFrom={setFrom} setTo={setTo} />
      <Callout tone="info">
        Esto no prueba que alguien se quede con algo: marca lo que conviene revisar. En naranja, quien está muy por encima del resto. Tocá un cajero
        para ver cada caso con su ticket.
      </Callout>
      <Card>
        <CardBody flush>
          {!q.data ? (
            <div style={{ padding: 16 }}>
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState icon={<UserRound />} title="No hubo ventas en estas fechas" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Cajero</th>
                    <th style={{ textAlign: 'right' }}>Ventas</th>
                    <th style={{ textAlign: 'right' }}>Anuladas</th>
                    <th style={{ textAlign: 'right' }}>Descuentos</th>
                    <th style={{ textAlign: 'right' }}>Pesos a mano</th>
                    <th style={{ textAlign: 'right' }}>Etiqueta repetida</th>
                    <th style={{ textAlign: 'right' }}>Retiros</th>
                    <th style={{ textAlign: 'right' }}>Cajón sin venta</th>
                    <th style={{ textAlign: 'right' }}>Faltó en caja</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.userId} data-clickable="true" onClick={() => setOpen(c)}>
                      <td>
                        <span className="bf-cell-main">{c.name}</span>
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {c.sales}
                        <div className="bf-cell-sub">{formatMoney(c.salesCents)}</div>
                      </td>
                      <Cell warn={high(c, 'voids')} n={c.voids} sub={c.voids ? formatMoney(c.voidsCents) : null} />
                      <Cell warn={high(c, 'discounts')} n={c.discounts} sub={c.discounts ? formatMoney(c.discountsCents) : null} />
                      <Cell
                        warn={high(c, 'manual')}
                        n={c.manualWeights}
                        sub={c.weightLines ? `${pct(c.manualWeights / c.weightLines)} de lo pesado` : null}
                      />
                      <Cell warn={c.repeatedLabels > 0} n={c.repeatedLabels} sub={null} />
                      <Cell warn={false} n={c.withdrawals} sub={c.withdrawals ? formatMoney(c.withdrawalsCents) : null} />
                      <Cell warn={false} n={c.drawerOpens} sub={null} />
                      <Cell warn={c.cashShortCents > 0} n={c.cashShortSessions} sub={c.cashShortCents ? formatMoney(c.cashShortCents) : null} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      <p className="bf-cell-sub">
        Pesos a mano: renglones por kilo cargados sin la etiqueta de la balanza. Etiqueta repetida: la misma etiqueta cobrada dos veces en 24 horas.
        Cajón sin venta: veces que se abrió con el botón “Abrir cajón” sin cobrar nada (cada una con su motivo; tocá el cajero para verlos). Las
        aperturas al cobrar una venta, para dar el vuelto, no cuentan. Si el cajón se abre con la llave no queda registrado: conviene que la llave la
        tenga solo el dueño o el encargado.
      </p>
      {open && <CashierDialog cashier={open} from={from} to={to} onClose={() => setOpen(null)} />}
    </>
  );
}

function Cell({ warn, n, sub }: { warn: boolean; n: number; sub: string | null }) {
  return (
    <td className={`bf-num ${warn ? 'bf-text-warning' : ''}`} style={{ textAlign: 'right', fontWeight: warn ? 600 : undefined }}>
      {n || '—'}
      {sub && <div className="bf-cell-sub">{sub}</div>}
    </td>
  );
}

function CashierDialog({ cashier, from, to, onClose }: { cashier: CashierControlDTO; from: string; to: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['control', 'cashier', cashier.userId, from, to],
    queryFn: () => api.get<SuspiciousEventDTO[]>(`/control/cashiers/${cashier.userId}?from=${from}&to=${to}`),
  });
  const [type, setType] = useState<string>('');
  const list = (q.data ?? []).filter((e) => !type || e.type === type);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={cashier.name} description={`Del ${formatDate(from)} al ${formatDate(to)}`} wide>
      <div className="bf-form">
        <Select value={type} onChange={(e) => setType(e.target.value)} aria-label="Tipo de movimiento" style={{ maxWidth: 280 }}>
          <option value="">Todos los movimientos</option>
          {Object.entries(SUSPICIOUS_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </Select>
        {!q.data ? (
          <Skeleton style={{ height: 160 }} />
        ) : list.length === 0 ? (
          <p className="bf-cell-sub">Nada para revisar.</p>
        ) : (
          <div className="bf-scroll-box bf-cut-table">
            <table className="bf-table">
              <tbody>
                {list.map((e, i) => (
                  <tr key={i}>
                    <td className="bf-cell-sub" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.at)}
                    </td>
                    <td>
                      <span className="bf-cell-main">{SUSPICIOUS_LABELS[e.type]}</span>
                      {e.ticket && <span className="bf-cell-sub"> · ticket {e.ticket}</span>}
                      <div className="bf-cell-sub">{e.detail}</div>
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {e.amountCents !== null ? formatMoney(e.amountCents) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="bf-form-actions">
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
